import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisconnect } from 'wagmi';
import {
  authService,
  persistUserToLocalStorage,
} from '../services/authService';
import { apiService } from '../services/api';
import { profileService } from '../services/profileService';
import { clearServerFlags, syncServerFlags } from '../feature-flag/serverFlags';
import {
  LoginRequest,
  SignupRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../types/auth';
import { toast } from 'sonner';
import { useApiError } from '@/hooks/useApiError';
import { useTranslation } from 'react-i18next';
import { clearAcademyCart } from './academy/cart/useAcademyCart';
import { academyEnrollmentsQueryKey } from './academy/data/useAcademy';
import { careerFitLatestResultQueryKey } from './academy/data/useCareerFit';

/**
 * Pages that use `?token=` for their own purpose. The social-login check below treats any `token`
 * param as an OAuth return, so it must skip these: otherwise it strips the query string the page
 * still needs and flashes a "signing you in" overlay at someone who never signed in.
 */
const TOKEN_OWNING_PATHS = ['/delete-account', '/unsubscribe'];

export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoginLoading, setSocialLoginLoading] = useState(false);
  const queryClient = useQueryClient();
  const { handleApiError } = useApiError();
  const handleApiErrorRef = useRef(handleApiError);
  handleApiErrorRef.current = handleApiError;
  const { t } = useTranslation('common');
  const { t: tAuth } = useTranslation('auth');

  // Check for social login redirect on component mount
  useEffect(() => {
    const checkSocialLoginRedirect = () => {
      if (
        TOKEN_OWNING_PATHS.some(path =>
          window.location.pathname.startsWith(path),
        )
      ) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthParams =
        urlParams.has('accessToken') ||
        urlParams.has('token') ||
        urlParams.has('code') ||
        urlParams.has('state');

      if (hasAuthParams) {
        setSocialLoginLoading(true);

        // Clear URL parameters after processing
        const newUrl = new URL(window.location.href);
        newUrl.search = '';
        window.history.replaceState({}, '', newUrl.toString());

        toast.success(t('auth.socialLoginSuccess'));

        // Check if there's a pending redirect
        const pendingRedirect = sessionStorage.getItem('pendingRedirect');
        if (pendingRedirect) {
          sessionStorage.removeItem('pendingRedirect');
          // Redirect to the pending invite after a short delay to allow user data to load
          setTimeout(() => {
            window.location.href = pendingRedirect;
          }, 1000);
        }
      }
    };

    checkSocialLoginRedirect();
  }, [t]);

  // Use React Query to manage user state with automatic re-fetching
  const {
    data: user,
    isLoading: isUserLoading,
    isError,
  } = useQuery({
    queryKey: ['user'],
    queryFn: () => {
      const storedUser = authService.getUser();
      if (!storedUser && authService.isAuthenticated()) {
        // If we have a token but no user data, fetch it
        return profileService.getCurrentUser().then(response => {
          persistUserToLocalStorage(response.result);
          return response.result;
        });
      }
      return storedUser;
    },
    enabled: authService.isAuthenticated(), // Only run query if authenticated
    staleTime: 30 * 60 * 1000, // 30 minutes - increased to reduce API calls
    gcTime: 60 * 60 * 1000, // 60 minutes - increased to keep data longer
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnMount: false, // Don't refetch on mount if data is fresh (Profile page handles its own refresh)
  });

  // Watch for changes in user data loading to manage social login loading state
  useEffect(() => {
    if (socialLoginLoading && !isUserLoading && (user || isError)) {
      // Add a small delay to ensure the loading state is visible for better UX
      setTimeout(() => {
        setSocialLoginLoading(false);
      }, 500);
    }
  }, [socialLoginLoading, isUserLoading, user, isError]);

  const isInviteLink = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.has('joinClanId') ||
      params.has('joinTeamId') ||
      params.has('joinOrgId') ||
      params.has('wildcardInviteId') ||
      params.has('inviteId') ||
      params.has('redirectTo')
    );
  };

  const login = async (credentials: LoginRequest) => {
    setIsLoading(true);
    try {
      if (isInviteLink()) {
        sessionStorage.setItem('pendingRedirect', window.location.href);
      }

      const response = await authService.signIn(credentials);
      // Immediately set user data in React Query cache
      if (response.result?.user) {
        queryClient.setQueryData(['user'], response.result.user);
      }
      // Invalidate and refetch user data
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({
        queryKey: academyEnrollmentsQueryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: careerFitLatestResultQueryKey,
      });

      // Only a response carrying tokens is a real sign-in — a tokenless 200
      // must never look successful.
      if (response.result?.accessToken) {
        toast.success(t('auth.loginSuccess'));
        // Region-gated flags are per-user; fetch them for the new session.
        void syncServerFlags();
      }

      return response;
    } catch (error) {
      handleApiError(error, {
        defaultMessage: t('auth.loginFailed'),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (credentials: SignupRequest) => {
    setIsLoading(true);
    try {
      const response = await authService.signUp(credentials);
      // Auto-login after successful signup if access token is provided
      if (response.result?.accessToken && response.result?.user) {
        // Invalidate and refetch user data
        await queryClient.invalidateQueries({ queryKey: ['user'] });

        toast.success(t('auth.signupSuccessAutoLogin'));
      } else {
        toast.success(t('auth.signupSuccessVerifyEmail'));
      }
      return response;
    } catch (error) {
      handleApiError(error, {
        defaultMessage: t('auth.signupFailed'),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = () => {
    const backendUrl = import.meta.env.VITE_API_BASE_URL;
    if (!backendUrl) {
      alert('API base URL is not configured.');
      return;
    }
    // Only store if this is an invite link
    if (isInviteLink()) {
      sessionStorage.setItem('pendingRedirect', window.location.href);
    }
    const currentUrl = window.location.origin;
    const googleAuthUrl = `${backendUrl}/public/api/v1/auth/google?redirectUrl=${encodeURIComponent(
      currentUrl,
    )}`;
    window.location.href = googleAuthUrl;
  };

  const { disconnect: disconnectWallet } = useDisconnect();

  const logout = async () => {
    try {
      try {
        disconnectWallet();
      } catch (error) {
        console.warn('Error disconnecting wallet:', error);
      }

      // Revoke tokens server-side (best-effort — don't block logout on failure)
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        await apiService.post('/public/api/v1/auth/signOut', { refreshToken });
      } catch {
        // Best-effort — server-side revocation failed, but client logout continues
      }

      authService.signOut();
      clearAcademyCart();
      clearServerFlags();

      queryClient.setQueryData(['user'], null);
      queryClient.removeQueries({ queryKey: ['user'] });
      queryClient.removeQueries({ queryKey: ['wallets'] });
      queryClient.removeQueries({ queryKey: academyEnrollmentsQueryKey });
      queryClient.removeQueries({ queryKey: careerFitLatestResultQueryKey });

      toast.success(t('auth.logoutSuccess'));
    } catch (error) {
      console.error('Error during logout:', error);
      authService.signOut();
      clearAcademyCart();
      clearServerFlags();
      queryClient.setQueryData(['user'], null);
      queryClient.removeQueries({ queryKey: ['user'] });
      queryClient.removeQueries({ queryKey: ['wallets'] });
      queryClient.removeQueries({ queryKey: academyEnrollmentsQueryKey });
      queryClient.removeQueries({ queryKey: careerFitLatestResultQueryKey });
      toast.success(t('auth.logoutSuccess'));
    }
  };

  // Ref keeps `handleApiError` off the dependency array — useApiError returns a new fn each render.
  const refreshUser = useCallback(async () => {
    if (!authService.isAuthenticated()) return;
    try {
      const response = await profileService.getCurrentUser();
      queryClient.setQueryData(['user'], response.result);
      persistUserToLocalStorage(response.result);
    } catch (error) {
      handleApiErrorRef.current(error, {
        defaultMessage: tAuth('errors.refreshUserFailed'),
        showToast: false,
      });
    }
  }, [queryClient]);

  const forgotPassword = async (request: ForgotPasswordRequest) => {
    setIsLoading(true);
    try {
      const response = await authService.forgotPassword(request);
      toast.success(
        response.message || tAuth('forgotPassword.toast.success.linkSent'),
      );
      return response;
    } catch (error) {
      handleApiError(error, {
        defaultMessage: tAuth('forgotPassword.toast.error.linkFailed'),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (request: ResetPasswordRequest) => {
    setIsLoading(true);
    try {
      const response = await authService.resetPassword(request);
      toast.success(
        response.message || tAuth('setPassword.toast.success.passwordReset'),
      );
      return response;
    } catch (error) {
      handleApiError(error, {
        defaultMessage: tAuth('setPassword.toast.error.resetFailed'),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    login,
    signup,
    loginWithGoogle: loginWithGoogle,
    logout,
    refreshUser,
    forgotPassword,
    resetPassword,
    isLoading: isLoading || isUserLoading,
    socialLoginLoading,
    isAuthenticated: authService.isAuthenticated(),
  };
};
